<template>
  <form-block :title="tr('Placement')">
    <info-text v-if="disabledText">
      {{ disabledText }}
    </info-text>

    <ui-radio-group
      v-else
      :model-value="isAutoPlacement ? 'auto' : 'manual'"
      :disabled="disabled"
      class="flex flex-col gap-3"
      @update:model-value="(value) => isAutoPlacement = value === 'auto'"
    >
      <div class="flex flex-col gap-1">
        <label class="flex w-fit cursor-pointer items-center gap-2 text-sm font-medium text-foreground">
          <ui-radio-group-item value="auto" :disabled="entity.isAppPromo || disabled" />
          {{ tr('Automatic') }}
        </label>
        <p class="m-0 pl-6 text-sm text-muted-foreground">{{ tr('Maximize your budget and help show your ads to more people. Facebook\'s delivery system will allocate your ad set\'s budget across multiple placements based on where they\'re likely to perform best.') }}</p>
      </div>
      <div class="flex flex-col gap-1">
        <label class="flex w-fit cursor-pointer items-center gap-2 text-sm font-medium text-foreground">
          <ui-radio-group-item value="manual" />
          {{ tr('Manual') }}
        </label>
        <p class="m-0 pl-6 text-sm text-muted-foreground">{{ tr('Manually choose the places to show your ad. The more placements you select, the more opportunities you\'ll have to reach your target audience and achieve your business goals.') }}</p>
      </div>
    </ui-radio-group>

    <template v-if="!isAutoPlacement">
      <div class="flex max-w-lg flex-col gap-4">
        <div class="flex flex-col gap-1.5">
          <ui-label>{{ tr('Devices') }}</ui-label>
          <select-field
            v-model="adsetTargetingDevices"
            :options="devices"
            :aria-label="tr('Devices')"
            :disabled="entity.isAppPromo || disabled"
          />
        </div>

        <div v-if="hasManualMobileTargeting" class="flex flex-col gap-1.5">
          <ui-label>{{ tr('Specific mobile devices') }}</ui-label>
          <select-field
            v-model="adsetTargetingUserOs"
            :options="mobileDevices"
            :aria-label="tr('Specific mobile devices')"
            :disabled="entity.isAppPromo || disabled"
          />
        </div>
      </div>

      <div class="flex flex-col gap-1.5">
        <ui-label>{{ tr('Platforms') }}</ui-label>
        <placement-selector :entity="entity" :disabled="disabled" />
      </div>
    </template>
  </form-block>
</template>

<script>
import { PLATFORMS } from '@/consts';
import RuleFormBlockMixin from '@/mixins/RuleForm/Block.js';
import InfoText from '@/ui/components/InfoText.vue';
import PlacementSelector from '@/ui/components/RuleForm/PlacementSelector.vue';
import SelectField from '@/ui/components/SelectField.vue';
import { storeUrlIsAppleAppStore, storeUrlIsGooglePlay } from '@/utils/facebook.js';

export default {
  name: 'AdPlacementBlock',
  components: {
    InfoText,
    PlacementSelector,
    SelectField,
  },
  mixins: [ RuleFormBlockMixin ],
  data: () => ({
    isAutoPlacement: false,
    adsetTargetingDevices: null,
    adsetTargetingUserOs: null,
  }),
  computed: {
    devices() {
      return this.$store.getters['app/devices'];
    },
    adPlacements() {
      return this.$store.getters['app/facebookAdPlacements'];
    },
    platforms() {
      return PLATFORMS;
    },
    mobileDevices() {
      return this.$store.getters['app/mobileDevices'];
    },
    hasManualMobileTargeting() {
      return this.entity.settings.adset_targeting_devices && this.entity.settings.adset_targeting_devices.includes('mobile');
    },
    disabledText() {
      if (this.entity.isInstagramProfileVisitCampaign) {
        return this.tr('For Instagram Profile Visit campaigns, ads appear only on Instagram, and this setting cannot be changed.');
      }
      if (this.entity.isPageLikeCampaign) {
        return this.tr('For Page Like engagement campaigns, ads appear only on Facebook, and this setting cannot be changed.');
      }
      if (this.entity.output_campaign.isObjectiveEngagement) {
        return this.tr('For engagement campaigns, we use platform-specific ad placement to optimize interactions. This means ads from Facebook posts will only run on Facebook, and Instagram posts only on Instagram. This setting ensures that likes and comments appear directly on your original posts.');
      }

      return null;
    },
  },
  watch: {
    disabledText: {
      immediate: true,
      handler(val) {
        if (val) {
          this.isAutoPlacement = true;
        }
      },
    },
    isAutoPlacement(val, oldVal) {
      if (val == oldVal) {
        return;
      }

      if (val) {
        this.entity.settings.adset_targeting_positions = {};

        this.platforms.forEach(platform => {
          this.entity.settings.adset_targeting_positions[platform.machineName] = [];
        });

        this.adsetTargetingDevices = [];
        this.adsetTargetingUserOs = [];
      }
      else {
        this.platforms.forEach(platform => {
          this.entity.settings.adset_targeting_positions[platform.machineName] = this.adPlacements[platform.machineName].map(i => i.machine_name);
        });

        this.adsetTargetingDevices = this.devices[0];
        this.adsetTargetingUserOs = this.mobileDevices[0];
      }
    },
    adsetTargetingDevices(val) {
      this.entity.settings.adset_targeting_devices = val ? val.machine_name : null;
    },
    adsetTargetingUserOs(val) {
      this.entity.settings.adset_targeting_user_os = val ? val.machine_name : null;
    },
    'entity.settings.adset_conversion_app_store_url'(val) {
      if (val) {
        if (storeUrlIsGooglePlay(val)) {
          this.adsetTargetingUserOs = this.mobileDevices.find(i => JSON.stringify(i.machine_name) === JSON.stringify([ 'Android' ]));
        }
        else if (storeUrlIsAppleAppStore(val)) {
          this.adsetTargetingUserOs = this.mobileDevices.find(i => JSON.stringify(i.machine_name) === JSON.stringify([ 'iOS' ]));
        }
      }
    },
  },
  created() {
    if (this.entity.settings.adset_targeting_devices) {
      this.adsetTargetingDevices = this.devices.find(i => JSON.stringify(i.machine_name) === JSON.stringify(this.entity.settings.adset_targeting_devices));
    }
    if (this.entity.settings.adset_targeting_user_os) {
      this.adsetTargetingUserOs = this.mobileDevices.find(i => JSON.stringify(i.machine_name) === JSON.stringify(this.entity.settings.adset_targeting_user_os));
    }

    if (this.entity.isAppPromo) {
      this.isAutoPlacement = false;
      this.adsetTargetingDevices = this.devices.find(i => JSON.stringify(i.machine_name) === JSON.stringify([ 'mobile' ]));
      if (
        this.entity.settings.adset_targeting_positions.facebook.length === 0
        && this.entity.settings.adset_targeting_positions.instagram.length === 0
      ) {
        this.entity.settings.adset_targeting_positions.facebook = this.$store.getters['app/facebookAdPlacements'].facebook.map(i => i.machine_name);
        this.entity.settings.adset_targeting_positions.instagram = this.$store.getters['app/facebookAdPlacements'].instagram.map(i => i.machine_name);
      }
    }
    else if (
      (!this.adsetTargetingDevices || this.adsetTargetingDevices.length === 0)
      && (!this.adsetTargetingUserOs || this.adsetTargetingUserOs.length === 0)
      && Object.values(this.entity.settings.adset_targeting_positions).filter(i => i.length !== 0).length === 0
    ) {
      this.isAutoPlacement = true;
    }
  },
};
</script>
